import { FC, useState } from 'react';
import styled from 'styled-components';

import { ButtonIw } from '@/_pages/interwill/components/ButtonIw';
import { useBeneficiariesActions } from '@/entities/beneficiary';
import {
  IwScenarioSettingsDetails,
  useInterwillsActions,
  useIwEditingStore,
  useIwExecutionStore,
} from '@/entities/interwills';
import { useScenariosStore } from '@/entities/scenario/scenarios.store';
import { mergeMaps } from '@/features/edit-scenarios/useSubmitScenario';
import { BeneficiariesDataMap } from '@/features/edit-scenarios/utils/getBeneficiariesDataMap';
import { updateScenarioCollectionItemsPublicKeys } from '@/features/edit-scenarios/utils/updateScenarioCollectionItemsPublicKeys';
import { useNotices } from '@/features/notices/useNotices';
import { ApiCreateScenarioDto, useApi } from '@/shared/api';
import { media } from '@/shared/components';
import { useAppRouter } from '@/shared/hooks/app/useAppRouter';
import { getRelatedToAssetDtosIds } from '@/shared/lib/helpers/scenario';
import { Permission } from '@/shared/lib/secure-json/core/secure-json-collection/types';
import { useSJDatabase } from '@/shared/lib/sj-orm/hooks/use-sj-database';
import { DtoId } from '@/shared/lib/sj-orm/models/base.dto';
import { CollectionName } from '@/shared/lib/sj-orm/types';
import { useKeyPairStore } from '@/shared/store/decrypted-keypair.store';
import { useTranslation } from '@/shared/utils/i18n/useTranslation';
import { useModalsStore } from '@/widgets/modals/modals.store';
import { ScenarioConfirmModalProps } from '@/widgets/modals/types';

import { ModalContainerV2 } from '../containers/ModalContainerV2';

export const ScenarioConfirmModal: FC<ScenarioConfirmModalProps> = ({ content, days, trigger }) => {
  const { t } = useTranslation('widgets/modals', { keyPrefix: 'scenarioConfirm' });
  const [loading, setLoading] = useState(false);

  const router = useAppRouter();

  const modals = useModalsStore();
  const { assets, beneficiaries, executor, subExecutor } = useIwEditingStore();
  const {
    setScenarioSettings,
    scenarioSettings,
    finalPdfUrl,
    signedWill,
    testatorVerification,
    witnessVerifications,
    videoCertificates,
    supportedDocuments,
    witnessDocuments,
  } = useIwExecutionStore();
  const { keyPair } = useKeyPairStore();
  const { addScenario: addScenarioToStore, updateScenario } = useScenariosStore();

  const { findById } = useBeneficiariesActions();
  const { getInterwill } = useInterwillsActions();

  const api = useApi();
  const sjDatabase = useSJDatabase();
  const notices = useNotices();

  const onClick = async () => {
    if (!scenarioSettings?.periodicDays || !scenarioSettings.transferDetails || !keyPair?.publicKey)
      return;

    setLoading(true);

    const will = await getInterwill();

    if (!will.centralDbInterwillId) return;

    const dtoIds: DtoId[] = [will.id];

    const seenIds = new Set<string>();
    const beneficiariesRes = await api.profilesGetBeneficiaries();
    const allBeneficiaries = beneficiariesRes.data ?? [];
    const beneficiariesProfiles = beneficiaries
      .map((item) => {
        if (item.id && !item.isUnreg && !seenIds.has(item.id)) {
          seenIds.add(item.id);
          return findById(item.id);
        }
        return undefined;
      })
      .filter((id) => id !== undefined);

    const hasUnregs = beneficiaries.some((item) => item.isUnreg);
    if (hasUnregs) {
      const executorData = executor?.id ? findById(executor?.id) : null;
      if (executorData && !seenIds.has(executorData.id)) {
        beneficiariesProfiles.push(executorData);
      }

      const subExecutorData = subExecutor?.id ? findById(subExecutor?.id) : null;
      if (subExecutorData && !seenIds.has(subExecutorData.id)) {
        beneficiariesProfiles.push(subExecutorData);
      }
    }

    const filePaths = [
      finalPdfUrl,
      signedWill?.url,
      testatorVerification?.filePath,
      testatorVerification?.generatedPdfPath,
      ...Object.values(witnessVerifications)
        .map((item) => [item.filePath, item.generatedPdfPath])
        .flat(),
      ...Object.values(videoCertificates).map((item) => item.filePath),
      ...Object.values(supportedDocuments).map((item) => item.url),
      ...Object.values(witnessDocuments).map((item) => item.filePath),
    ].filter((item) => typeof item === 'string');

    const fileMetaInfoDtoIds =
      sjDatabase.collections.fileMetaInfos
        ?.findMany((item) => filePaths.includes(item.filePath))
        .map((fileMetaInfo) => fileMetaInfo.id) ?? [];

    const encryptedFileKeysDtoIds =
      sjDatabase.collections.encryptedFileKey
        ?.findMany((encFileKey) => filePaths.some((filePath) => encFileKey.id.includes(filePath)))
        .map((encFileKey) => encFileKey.id) ?? [];

    const filteredBeneficiaries = allBeneficiaries.filter((beneficiary) =>
      beneficiariesProfiles.some((profile) => profile.centralDbProfileId === beneficiary.id),
    );

    filteredBeneficiaries?.forEach((beneficiary) => {
      if (!beneficiary.publicKey) return;

      encryptedFileKeysDtoIds.forEach((item) => {
        if (!beneficiary.publicKey) return;
        sjDatabase.collections.encryptedFileKey?.putPublicKey(item, beneficiary.publicKey);
        sjDatabase.collections.encryptedFileKey?.putPermissions(item, beneficiary.publicKey, [
          Permission.READ,
        ]);
      });

      sjDatabase.collections.interwills?.putPublicKey(will.id, beneficiary.publicKey);
    });

    dtoIds.push(...fileMetaInfoDtoIds, ...encryptedFileKeysDtoIds);

    const beneficiariesDataMap = beneficiariesProfiles.reduce<BeneficiariesDataMap>((acc, item) => {
      const beneficiaryDto = filteredBeneficiaries?.find(
        (dto) => dto.id === item.centralDbProfileId,
      );
      if (!item.id || !beneficiaryDto) return acc;
      acc[item.id] = {
        centralDbId: item.centralDbProfileId,
        dtoId: item.id,
        publicKey: beneficiaryDto.publicKey ?? undefined,
      };
      return acc;
    }, {});

    if (scenarioSettings.transferDetails === IwScenarioSettingsDetails.WillInstructionAsset) {
      const relatedDtoIdsMapList = assets.map((assetDto) =>
        getRelatedToAssetDtosIds({
          assetDtoId: assetDto.id,
          sjDb: sjDatabase,
          includeAsset: true,
        }),
      );

      const relatedDtoIdsMap = mergeMaps(relatedDtoIdsMapList);

      dtoIds.push(...Array.from(relatedDtoIdsMap.values()).flat());

      await updateScenarioCollectionItemsPublicKeys({
        relatedDtoIdsMap: relatedDtoIdsMap,
        beneficiariesDataMap,
        sjDb: sjDatabase,
      });
    }

    const permissions = [];
    if (executor?.id) {
      const profileId = findById(executor.id)?.centralDbProfileId;
      if (profileId) permissions.push(profileId);
    }
    let response: Awaited<ReturnType<typeof api.scenarioCreate>>;
    const data: ApiCreateScenarioDto = {
      dtoIds,
      triggers: {
        inactivity: {
          afterDays: Number(scenarioSettings.periodicDays),
        },
      },
      triggerCondition: 'ONE_OF',
      transferCondition: 'ALL_OF',
      transfers: beneficiariesProfiles.map((item) => ({
        recipientProfileId: item.centralDbProfileId,
      })),
      permissions,
    };

    if (scenarioSettings.id) {
      response = await api.scenarioEdit(scenarioSettings.id, data);
    } else {
      response = await api.scenarioCreate(data);
    }

    if (!response.data || response.error) {
      notices.error(response.error?.message);
      router.push('/interwill');
      modals.closeModal();
      return;
    }

    const activationResponse = await api.scenarioActivate(response.data?.id);

    if (!activationResponse.data || activationResponse.error) {
      notices.error(activationResponse.error?.message);
      router.push('/interwill');
      modals.closeModal();
      return;
    }

    if (scenarioSettings.id) {
      updateScenario(activationResponse.data);
    } else {
      await sjDatabase.collections.scenarioMetaInfos?.create(
        {
          id: sjDatabase.generateId(CollectionName.SCENARIO_META_INFO),
          centralDbId: response.data.id,
          name: 'Will transfer algorithm',
        },
        [keyPair?.publicKey],
      );
      await api.willsAttachScenario(will.centralDbInterwillId, {
        scenarioId: response.data?.id,
      });
      addScenarioToStore(activationResponse.data);
    }

    setScenarioSettings({
      id: response.data?.id,
      status: 'active',
      beneficiaries: Object.values(beneficiariesDataMap).map((item) => ({
        dtoId: item.dtoId,
        centralProfileId: item.centralDbId,
      })),
    });
    router.push('/interwill');
    modals.closeModal();
  };

  return (
    <ModalContainerV2 pinBottom title={t('title')}>
      <InnerForm>
        <span>{t('text')}</span>
        <List>
          <span>{t.rich('trigger', { trigger })}</span>
          <span>{t.rich('days', { days })}</span>
          <span>{t.rich('content', { content })}</span>
        </List>
        <span>{t('cannotBeEdited')}</span>
      </InnerForm>
      <Buttons>
        <ButtonIw onClick={modals.closeModal} type="default">
          {t('cancelBtn')}
        </ButtonIw>
        <ButtonIw loading={loading} disabled={loading} onClick={onClick}>
          {t('confirmBtn')}
        </ButtonIw>
      </Buttons>
    </ModalContainerV2>
  );
};

const InnerForm = styled.div`
  display: flex;
  flex-direction: column;
  overflow: auto;
  gap: 24px;
  padding: 24px 0;
  font-size: 14px;
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Buttons = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 16px;

  ${media.iwMobile} {
    flex-direction: column-reverse;
  }
`;
